import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, Snackbar } from '@bolteu/clay-react';

import { clearNotificationAction, selectNotification } from 'rdx/notifications';

const NOTIFICATION_ROOT_ATTRIBUTE = 'data-notification-root';

function NotificationsContent() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const notification = useSelector(selectNotification);
  const { toasts, add, close } = Snackbar.useToastManager();

  const handleClose = useCallback(() => {
    dispatch(clearNotificationAction());
  }, [dispatch]);

  const message = notification
    ? notification.message ?? intl.formatMessage({ id: notification.intlId })
    : '';

  const closeButtonText = notification
    ? notification.closeButtonText ??
      intl.formatMessage({
        id: notification.closeButtonIntlId || 'components.Notifications.clear',
      })
    : '';

  const severity = notification?.severity;
  const isOpen = Boolean(notification);

  useEffect(() => {
    if (!isOpen || severity) {
      return undefined;
    }
    const toastId = add({
      description: message,
      timeout: 0,
      actionProps: { children: closeButtonText, onClick: handleClose },
    });
    return () => close(toastId);
  }, [isOpen, severity, message, closeButtonText, handleClose, add, close]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleClickAway = (event: MouseEvent) => {
      if (!(event.target as Element | null)?.closest?.(`[${NOTIFICATION_ROOT_ATTRIBUTE}]`)) {
        handleClose();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    const timeoutId = setTimeout(() => document.addEventListener('click', handleClickAway), 0);
    document.addEventListener('keydown', handleEscape);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickAway);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClose]);

  return (
    <>
      {isOpen && severity ? (
        <div className="pointer-events-none fixed inset-x-0 max-sm:bottom-2 z-[1400] flex justify-center px-2 sm:bottom-6">
          <div
            {...{ [NOTIFICATION_ROOT_ATTRIBUTE]: '' }}
            className="pointer-events-auto max-w-full"
          >
            <Alert.Root severity={severity}>
              <Alert.Icon />
              <Alert.Content>
                <Alert.Description>{message}</Alert.Description>
              </Alert.Content>
            </Alert.Root>
          </div>
        </div>
      ) : null}
      {toasts.length > 0 ? (
        <Snackbar.Portal>
          <Snackbar.Viewport placement="bottom-center">
            {toasts.map((toast) => (
              <Snackbar.Root
                key={toast.id}
                toast={toast}
                {...{ [NOTIFICATION_ROOT_ATTRIBUTE]: '' }}
              >
                <Snackbar.Content>
                  <Snackbar.Description className="whitespace-pre-wrap" />
                  <Snackbar.Action />
                </Snackbar.Content>
              </Snackbar.Root>
            ))}
          </Snackbar.Viewport>
        </Snackbar.Portal>
      ) : null}
    </>
  );
}

export function Notifications() {
  return (
    <Snackbar.Provider timeout={0}>
      <NotificationsContent />
    </Snackbar.Provider>
  );
}
